import { Injectable, Injector } from '@angular/core'
import { Functions, httpsCallable } from '@angular/fire/functions'
import { SlackChannel, WatiTemplates } from '@parkupp/core'
import { CampaignTrackingService } from './campaign-tracking.service'
import { ModernAnalyticsService } from './modern-analytics.service'

@Injectable({
    providedIn: 'root',
})
export class BackendService {
    constructor(private functions: Functions, private injector: Injector) {}

    // Resolved lazily: CampaignTrackingService -> GlobalService -> AuthenticationService -> BackendService is a DI cycle
    private get campaignTrackingService(): CampaignTrackingService {
        return this.injector.get(CampaignTrackingService)
    }

    async httpSendSlack(post: { message: string; channel?: SlackChannel }): Promise<any> {
        try {
            const callable = httpsCallable(this.functions, 'httpSendSlack')
            const result = await callable(post) // Invoke the function and await the Promise
            return result.data // Assuming the data you want is stored in the `data` property
        } catch (error) {
            console.error('Error when calling void payment function:', error)
            throw new Error('Failed to invoke void payment function')
        }
    }

    async httpCreateException(post: { message: string; json: string | object }): Promise<any> {
        try {
            // Check if 'json' is an object and stringify it if necessary
            if (typeof post.json === 'object') {
                post = {
                    ...post, // Spread the existing properties
                    json: JSON.stringify(post.json), // Convert 'json' to a string
                }
            }

            const callable = httpsCallable(this.functions, 'httpCreateException')
            const result = await callable(post) // Invoke the function and await the Promise
            return result.data // Assuming the data you want is stored in the `data` property
        } catch (error) {
            console.error('Error when calling void payment function:', error)
        }
    }

    async httpSettlementRequest(subscriptionKey: string): Promise<any> {
        try {
            const callable = httpsCallable(this.functions, 'payGate-httpSettlementRequest')
            const result = await callable({ subscriptionKey })
            return result.data
        } catch (error) {
            console.error('Error when calling settlement request function:', error)
            throw error
        }
    }

    async httpVoidRequest(
        subscriptionKey: string,
        rejectionReason?: string,
        options?: { cancelledBy?: 'client'; auditEvent?: Record<string, any> | null }
    ): Promise<any> {
        try {
            const callable = httpsCallable(this.functions, 'payGate-httpVoidRequest')
            const result = await callable({
                subscriptionKey,
                rejectionReason,
                ...(options?.cancelledBy ? { cancelledBy: options.cancelledBy } : {}),
                ...(options?.auditEvent ? { auditEvent: options.auditEvent } : {}),
            })
            return result.data
        } catch (error) {
            console.error('Error when calling void request function:', error)
            throw error
        }
    }

    async httpAdminRetryFirstPayment(subscriptionKey: string): Promise<any> {
        const callable = httpsCallable(this.functions, 'payGate-httpAdminRetryFirstPayment')
        const result = await callable({ subscriptionKey })
        return result.data
    }

    async httpAdminCancelFirstPayment(subscriptionKey: string): Promise<any> {
        const callable = httpsCallable(this.functions, 'payGate-httpAdminCancelFirstPayment')
        const result = await callable({ subscriptionKey })
        return result.data
    }

    async httpVoidRequestManually(paymentKey: string): Promise<any> {
        try {
            const callable = httpsCallable(this.functions, 'httpVoidRequestManually')
            const result = await callable({ paymentKey })
            return result.data
        } catch (error) {
            console.error('Error when calling void payment function:', error)
        }
    }

    async syncAdminClientsAuthData(): Promise<any> {
        try {
            // Set timeout to 10 minutes (600000 ms) to match server-side timeout
            const callable = httpsCallable(this.functions, 'syncAdminClientsAuthData', {
                timeout: 600000, // 10 minutes in milliseconds
            })
            const result = await callable({})
            return result.data
        } catch (error) {
            console.error('Error when calling syncAdminClientsAuthData function:', error)
            throw new Error('Failed to invoke syncAdminClientsAuthData function')
        }
    }

    async syncAdminClientsSubscriptionData(): Promise<any> {
        try {
            const callable = httpsCallable(this.functions, 'syncAdminClientsSubscriptionData', {
                timeout: 540000,
            })
            const result = await callable({})
            return result.data
        } catch (error: any) {
            console.error('Error when calling syncAdminClientsSubscriptionData function:', error)
            console.error('Error details:', error.details)
            throw error
        }
    }

    async refreshParkingInsights(): Promise<any> {
        return (await httpsCallable(this.functions, 'refreshParkingInsights', { timeout: 540000 })({})).data
    }

    async refreshGoogleReviews(): Promise<any> {
        return (await httpsCallable(this.functions, 'refreshGoogleReviews', { timeout: 120000 })({})).data
    }

    /** Close last month and create affiliate statements (manual only; there is no schedule). */
    async generateAffiliateStatements(): Promise<any> {
        return (await httpsCallable(this.functions, 'affiliateAdminGenerateStatements', { timeout: 540000 })({})).data
    }

    async backfillClientAttribution(apply: boolean): Promise<any> {
        return (await httpsCallable(this.functions, 'backfillClientAttribution', { timeout: 540000 })({ apply })).data
    }

    // Test route functions
    async testSubscriptionExpirationReport(): Promise<any> {
        try {
            const callable = httpsCallable(this.functions, 'testRoutes-testSubscriptionExpirationReport', {
                timeout: 540000,
            })
            const result = await callable({})
            return result.data
        } catch (error: any) {
            console.error('Error when calling testSubscriptionExpirationReport:', error)
            console.error('Error details:', error.details)
            throw error
        }
    }

    async testMerchantRejectionsReport(): Promise<any> {
        try {
            const callable = httpsCallable(this.functions, 'testRoutes-testMerchantRejectionsReport', {
                timeout: 540000,
            })
            const result = await callable({})
            return result.data
        } catch (error: any) {
            console.error('Error when calling testMerchantRejectionsReport:', error)
            console.error('Error details:', error.details)
            throw error
        }
    }

    async testDailyEmailLeadsReport(): Promise<any> {
        try {
            const callable = httpsCallable(this.functions, 'testRoutes-testDailyEmailLeadsReport', {
                timeout: 540000,
            })
            const result = await callable({})
            return result.data
        } catch (error: any) {
            console.error('Error when calling testDailyEmailLeadsReport:', error)
            console.error('Error details:', error.details)
            throw error
        }
    }

    async testDailySalesBellReport(): Promise<any> {
        try {
            const callable = httpsCallable(this.functions, 'testRoutes-testDailySalesBellReport', {
                timeout: 540000,
            })
            const result = await callable({})
            return result.data
        } catch (error: any) {
            console.error('Error when calling testDailySalesBellReport:', error)
            console.error('Error details:', error.details)
            throw error
        }
    }

    async testDailyAgingInvoicesReport(): Promise<any> {
        try {
            const callable = httpsCallable(this.functions, 'testRoutes-testDailyAgingInvoicesReport', {
                timeout: 540000,
            })
            const result = await callable({})
            return result.data
        } catch (error: any) {
            console.error('Error when calling testDailyAgingInvoicesReport:', error)
            console.error('Error details:', error.details)
            throw error
        }
    }

    async testSubscriptionEndingReminder(): Promise<any> {
        try {
            const callable = httpsCallable(this.functions, 'testRoutes-testSubscriptionEndingReminder', {
                timeout: 540000,
            })
            const result = await callable({})
            return result.data
        } catch (error: any) {
            console.error('Error when calling testSubscriptionEndingReminder:', error)
            console.error('Error details:', error.details)
            throw error
        }
    }

    async testWhatsappNoParkingUsers(): Promise<any> {
        try {
            const callable = httpsCallable(this.functions, 'testWhatsappNoParkingUsers', {
                timeout: 540000,
            })
            const result = await callable({})
            return result.data
        } catch (error: any) {
            console.error('Error when calling testWhatsappNoParkingUsers:', error)
            console.error('Error details:', error.details)
            throw error
        }
    }

    async testCloneClientsToAdminClients(): Promise<any> {
        try {
            const callable = httpsCallable(this.functions, 'testCloneClientsToAdminClients', {
                timeout: 540000,
            })
            const result = await callable({})
            return result.data
        } catch (error: any) {
            console.error('Error when calling testCloneClientsToAdminClients:', error)
            console.error('Error details:', error.details)
            throw error
        }
    }

    async testCloneMerchantsToMerchantsPrivate(): Promise<any> {
        try {
            const callable = httpsCallable(this.functions, 'testCloneMerchantsToMerchantsPrivate', {
                timeout: 540000,
            })
            const result = await callable({})
            return result.data
        } catch (error: any) {
            console.error('Error when calling testCloneMerchantsToMerchantsPrivate:', error)
            console.error('Error details:', error.details)
            throw error
        }
    }

    async testSaveDashboardData(): Promise<any> {
        try {
            const callable = httpsCallable(this.functions, 'testSaveDashboardData', {
                timeout: 540000,
            })
            const result = await callable({})
            return result.data
        } catch (error: any) {
            console.error('Error when calling testSaveDashboardData:', error)
            console.error('Error details:', error.details)
            throw error
        }
    }

    async testCronGenerateInvoices(): Promise<any> {
        try {
            const callable = httpsCallable(this.functions, 'testCronGenerateInvoices', {
                timeout: 540000,
            })
            const result = await callable({})
            return result.data
        } catch (error: any) {
            console.error('Error when calling testCronGenerateInvoices:', error)
            console.error('Error details:', error.details)
            throw error
        }
    }

    async testCronProcessInvoicePayments(): Promise<any> {
        try {
            const callable = httpsCallable(this.functions, 'testCronProcessInvoicePayments', {
                timeout: 540000,
            })
            const result = await callable({})
            return result.data
        } catch (error: any) {
            console.error('Error when calling testCronProcessInvoicePayments:', error)
            console.error('Error details:', error.details)
            throw error
        }
    }

    async testCronCheckDueInvoices(): Promise<any> {
        try {
            const callable = httpsCallable(this.functions, 'testCronCheckDueInvoices', {
                timeout: 540000,
            })
            const result = await callable({})
            return result.data
        } catch (error: any) {
            console.error('Error when calling testCronCheckDueInvoices:', error)
            console.error('Error details:', error.details)
            throw error
        }
    }

    async testCronSendM2MRenewalReminders(): Promise<any> {
        try {
            const callable = httpsCallable(this.functions, 'testCronSendM2MRenewalReminders', {
                timeout: 540000,
            })
            const result = await callable({})
            return result.data
        } catch (error: any) {
            console.error('Error when calling testCronSendM2MRenewalReminders:', error)
            console.error('Error details:', error.details)
            throw error
        }
    }

    async testCronUpdateMerchantResponseTime(): Promise<any> {
        try {
            const callable = httpsCallable(this.functions, 'testCronUpdateMerchantResponseTime', {
                timeout: 540000,
            })
            const result = await callable({})
            return result.data
        } catch (error: any) {
            console.error('Error when calling testCronUpdateMerchantResponseTime:', error)
            console.error('Error details:', error.details)
            throw error
        }
    }

    async testCronSubscriptionExpired(): Promise<any> {
        try {
            const callable = httpsCallable(this.functions, 'schedulers-testCronSubscriptionExpired', {
                timeout: 540000,
            })
            const result = await callable({})
            return result.data
        } catch (error: any) {
            console.error('Error when calling testCronSubscriptionExpired:', error)
            console.error('Error details:', error.details)
            throw error
        }
    }

    async testCronRetryFirstPayments(): Promise<any> {
        try {
            const callable = httpsCallable(this.functions, 'schedulers-testCronRetryFirstPayments', {
                timeout: 540000,
            })
            const result = await callable({})
            return result.data
        } catch (error: any) {
            console.error('Error when calling testCronRetryFirstPayments:', error)
            throw error
        }
    }

    async sendReviewRequests(): Promise<any> {
        try {
            const callable = httpsCallable(this.functions, 'testSendReviewRequests', {
                timeout: 540000,
            })
            const result = await callable({})
            return result.data
        } catch (error: any) {
            console.error('Error when calling sendReviewRequests:', error)
            console.error('Error details:', error.details)
            throw error
        }
    }

    async migrateReviewRequested(): Promise<any> {
        try {
            const callable = httpsCallable(this.functions, 'migrateReviewRequested', {
                timeout: 540000,
            })
            const result = await callable({})
            return result.data
        } catch (error: any) {
            console.error('Error when calling migrateReviewRequested:', error)
            console.error('Error details:', error.details)
            throw error
        }
    }

    async resetIncorrectlyConvertedLeads(): Promise<any> {
        try {
            const callable = httpsCallable(this.functions, 'migrateResetIncorrectlyConvertedLeads', {
                timeout: 540000,
            })
            const result = await callable({})
            return result.data
        } catch (error: any) {
            console.error('Error when calling resetIncorrectlyConvertedLeads:', error)
            console.error('Error details:', error.details)
            throw error
        }
    }

    async closeOldLeads(): Promise<any> {
        try {
            const callable = httpsCallable(this.functions, 'migrateCloseOldLeads', {
                timeout: 540000,
            })
            const result = await callable({})
            return result.data
        } catch (error: any) {
            console.error('Error when calling closeOldLeads:', error)
            console.error('Error details:', error.details)
            throw error
        }
    }

    async syncGravatarAvatars(): Promise<any> {
        try {
            const callable = httpsCallable(this.functions, 'testSyncGravatarAvatars', {
                timeout: 540000,
            })
            const result = await callable({})
            return result.data
        } catch (error: any) {
            console.error('Error when calling syncGravatarAvatars:', error)
            console.error('Error details:', error.details)
            throw error
        }
    }

    async reconcileGoogleAdsConversions(apply = false, cursor: string | null = null): Promise<any> {
        const result = await httpsCallable(this.functions, 'reconcileGoogleAdsConversions', { timeout: 540000 })({ apply, cursor })
        return result.data
    }

    async verifyGoogleAdsConversions(): Promise<any> {
        const result = await httpsCallable(this.functions, 'verifyGoogleAdsConversions', { timeout: 540000 })({})
        return result.data
    }

    async uploadGoogleAdsConversions(validateOnly = true): Promise<any> {
        try {
            const callable = httpsCallable(this.functions, 'testUploadGoogleAdsConversions', {
                timeout: 540000,
            })
            const result = await callable({ validateOnly })
            return result.data
        } catch (error: any) {
            console.error('Error when calling uploadGoogleAdsConversions:', error)
            console.error('Error details:', error.details)
            throw error
        }
    }

    async reconcileAdmytAccess(): Promise<any> {
        try {
            const callable = httpsCallable(this.functions, 'testReconcileAdmytAccess', {
                timeout: 540000,
            })
            const result = await callable({})
            return result.data
        } catch (error: any) {
            console.error('Error when calling reconcileAdmytAccess:', error)
            console.error('Error details:', error.details)
            throw error
        }
    }

    async migrateOccupantsToParkers(): Promise<any> {
        try {
            const callable = httpsCallable(this.functions, 'httpMigrateOccupantsToParkers', {
                timeout: 540000,
            })
            const result = await callable({})
            return result.data
        } catch (error: any) {
            console.error('Error when calling migrateOccupantsToParkers:', error)
            console.error('Error details:', error.details)
            throw error
        }
    }

    async seedFaqs(): Promise<any> {
        try {
            const callable = httpsCallable(this.functions, 'seedFaqs', {
                timeout: 120000,
            })
            const result = await callable({})
            return result.data
        } catch (error: any) {
            console.error('Error when calling seedFaqs:', error)
            console.error('Error details:', error.details)
            throw error
        }
    }

    async repairEventLogTimestamps(subscriptionKey?: string): Promise<any> {
        try {
            const callable = httpsCallable(this.functions, 'repairEventLogTimestamps', {
                timeout: 540000,
            })
            const result = await callable(subscriptionKey ? { subscriptionKey } : {})
            return result.data
        } catch (error: any) {
            console.error('Error when calling repairEventLogTimestamps:', error)
            console.error('Error details:', error.details)
            throw error
        }
    }

    async syncAllAdminClientsToOmnisend(): Promise<any> {
        try {
            const callable = httpsCallable(this.functions, 'testSyncAllAdminClientsToOmnisend', {
                timeout: 540000,
            })
            const result = await callable({})
            return result.data
        } catch (error: any) {
            console.error('Error when calling syncAllAdminClientsToOmnisend:', error)
            console.error('Error details:', error.details)
            throw error
        }
    }

    async seedOmnisendProductCategories(): Promise<any> {
        try {
            const callable = httpsCallable(this.functions, 'seedOmnisendProductCategories')
            const result = await callable({})
            return result.data
        } catch (error: any) {
            console.error('Error when calling seedOmnisendProductCategories:', error)
            console.error('Error details:', error.details)
            throw error
        }
    }

    async sendCrmWhatsappTemplate(phoneNumber: string, templateName: WatiTemplates, parameters: { name: string; value: string }[]): Promise<any> {
        try {
            const callable = httpsCallable(this.functions, 'whatsapp-sendCrmWhatsappTemplate')
            const result = await callable({ phoneNumber, templateName, parameters })
            return result.data
        } catch (error) {
            console.error('Error sending CRM WhatsApp template:', error)
            throw error
        }
    }

    async testCronPollProspectScrapes(): Promise<any> {
        try {
            const callable = httpsCallable(this.functions, 'prospecting-testCronPollProspectScrapes', {
                timeout: 540000,
            })
            const result = await callable({})
            return result.data
        } catch (error: any) {
            console.error('Error when calling testCronPollProspectScrapes:', error)
            console.error('Error details:', error.details)
            throw error
        }
    }

    async sendMerchantMonthlyReport(merchantKey?: string): Promise<any> {
        try {
            const callable = httpsCallable(this.functions, 'testSendMerchantMonthlyReport', {
                timeout: 540000,
            })
            const result = await callable(merchantKey ? { merchantKey } : {})
            return result.data
        } catch (error: any) {
            console.error('Error sending merchant monthly report:', error)
            console.error('Error details:', error.details)
            throw error
        }
    }

    async previewMerchantMonthlyReport(merchantKey?: string): Promise<any> {
        try {
            const callable = httpsCallable(this.functions, 'testPreviewMerchantMonthlyReport', {
                timeout: 540000,
            })
            const result = await callable(merchantKey ? { merchantKey } : {})
            return result.data
        } catch (error: any) {
            console.error('Error previewing merchant monthly report:', error)
            console.error('Error details:', error.details)
            throw error
        }
    }

    /**
     * Generic function caller for Firebase callable functions
     * Used for enterprise migrations and other admin tasks
     */
    async callFunction(functionName: string, data: any, idToken?: string): Promise<any> {
        try {
            const callable = httpsCallable(this.functions, functionName, {
                timeout: 600000, // 10 minutes
            })
            const result = await callable(data)
            return result.data
        } catch (error: any) {
            console.error(`Error calling ${functionName}:`, error)
            console.error('Error details:', error.details)
            throw error
        }
    }

    async deleteCrmUsers(uids: string[]): Promise<{ deleted: number; skipped: number; skippedDetails: { uid: string; reason: string }[] }> {
        const callable = httpsCallable(this.functions, 'deleteCrmUsers', {
            timeout: 540000,
        })
        const result = await callable({ uids })
        return result.data as { deleted: number; skipped: number; skippedDetails: { uid: string; reason: string }[] }
    }

    async backfillParkingReports(): Promise<any> {
        try {
            const callable = httpsCallable(this.functions, 'backfillParkingReports', {
                timeout: 540000,
            })
            const result = await callable({})
            return result.data
        } catch (error: any) {
            console.error('Error backfilling parking reports:', error)
            throw error
        }
    }

    async verifyRecaptchaAndCreateUser(payload: { recaptchaToken: string; email: string; password: string }): Promise<{ customToken: string; uid: string }> {
        const callable = httpsCallable(this.functions, 'authentication-verifyRecaptchaAndCreateUser')
        const result = await callable(payload)
        return result.data as { customToken: string; uid: string }
    }

    async submitEnquiry(payload: {
        recaptchaToken: string
        name?: string
        email?: string
        phone?: string
        location?: string
        bays?: string
        detail?: string
        type?: string
        company?: string
        denormEnterpriseKey?: string
    }): Promise<{ successful: boolean; id: string }> {
        const callable = httpsCallable(this.functions, 'submitEnquiry')
        // Attach attribution so landlord/corporate enquiries can be credited to a campaign or affiliate
        const { p_source, p_campaign } = this.campaignTrackingService.getCampaignData()
        const affiliateCode = this.campaignTrackingService.getAffiliateCode()
        const result = await callable({ ...payload, p_source, p_campaign, affiliateCode })
        const response = result.data as { successful: boolean; id: string }
        if (response.successful) void this.injector.get(ModernAnalyticsService).trackEnhancedLead(payload.email)
        return response
    }

    async httpAdminTestTokenPayment(clientKey: string, paymentMethodKey: string, amountInCents: number): Promise<any> {
        try {
            const callable = httpsCallable(this.functions, 'payGate-httpAdminTestTokenPayment', { timeout: 540000 })
            const result = await callable({ clientKey, paymentMethodKey, amountInCents })
            return result.data
        } catch (error: any) {
            console.error('Error calling httpAdminTestTokenPayment:', error)
            throw error
        }
    }

    // --- PayGate Lab (PayWeb3 migration test harness) ---

    private async callPayGateLab(name: string, payload: any): Promise<any> {
        try {
            const callable = httpsCallable(this.functions, `payGateLab-${name}`, { timeout: 540000 })
            const result = await callable(payload)
            return result.data
        } catch (error: any) {
            console.error(`Error calling payGateLab-${name}:`, error)
            return { error: true, message: error?.message || 'Request failed' }
        }
    }

    httpLabTokeniseInit(clientKey: string, amountInCents: number): Promise<any> {
        return this.callPayGateLab('httpLabTokeniseInit', { clientKey, amountInCents })
    }

    httpLabTokenPayment(payload: {
        clientKey: string
        paymentMethodKey: string
        amountInCents: number
        sendCvv: boolean
        cvvToggle?: boolean
        recurringType?: string
        initiatedType?: string
        initialAuth?: boolean
        originalRecurringReferenceId?: string
    }): Promise<any> {
        return this.callPayGateLab('httpLabTokenPayment', payload)
    }

    httpLabCompleteSession(sessionKey: string): Promise<any> {
        return this.callPayGateLab('httpLabCompleteSession', { sessionKey })
    }

    httpLabLookupVault(clientKey: string, paymentMethodKey: string): Promise<any> {
        return this.callPayGateLab('httpLabLookupVault', { clientKey, paymentMethodKey })
    }

    httpLabSettle(transactionId: string): Promise<any> {
        return this.callPayGateLab('httpLabSettle', { transactionId })
    }

    httpLabVoid(transactionId: string): Promise<any> {
        return this.callPayGateLab('httpLabVoid', { transactionId })
    }
}
